import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi2";
import { MdOutlineCalendarToday, MdOutlineLocationOn } from "react-icons/md";
import { IoTicket } from "react-icons/io5";
import { MdVolumeOff, MdVolumeUp } from "react-icons/md";
import { ApiMediaURL } from "../../setting/GlobleVariable";
import { GetAllCraousalEvent } from "../../redux/actions/Admin/AdminActions";
import { resolveListingPrice } from "./MarketplaceNewUI/marketplaceHomeFeaturedFromApi";
import {
  applyStartingPrices,
  useBatchListingPrices,
} from "./MarketplaceNewUI/marketplaceDiscoverListingPrices";
import SkeletonLoading from "../Carousal/Skeleton";

const AUTOPLAY_INTERVAL = 6000;

/** Explicit height on mobile so bottom stack layout resolves correctly */
const HERO_SECTION_CLASS =
  "relative w-full overflow-hidden bg-black h-[52vw] min-h-[240px] max-h-[380px] sm:h-[58vw] sm:min-h-[300px] sm:max-h-[440px] md:h-auto md:min-h-0 md:aspect-[21/9] md:max-h-[85vh]";

const HERO_MEDIA_CLASS =
  "w-full h-full object-contain object-center transition-opacity duration-500";

const PLACEHOLDER_IMAGE =
  "https://images.unsplash.com/photo-1658046413536-6e5933dfd939?w=1080&q=80";

function mapCarouselItemToSlide(item, index) {
  const ev = item?.event?.[0];
  const name = ev?.name || "Featured event";
  const venueName = ev?.venue?.name || "";
  const locationStr = ev?.venue?.location || "";
  const occurs = ev?.occurs_at_local;

  const dateLine = occurs
    ? `${new Date(occurs).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })} · ${new Date(occurs).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      })}`
    : "—";

  const hasCarouselVideo = item?.mediaType === "video" && item?.video;
  const imageUrl = item?.image
    ? `${ApiMediaURL}/${item.image}`
    : hasCarouselVideo
      ? undefined
      : PLACEHOLDER_IMAGE;
  const videoUrl = hasCarouselVideo ? `${ApiMediaURL}/${item.video}` : undefined;

  const available = ev?.available_count;
  const availability =
    available != null && available !== ""
      ? `${Number(available) || available} tickets available`
      : "Tickets available";

  const startingPrice = resolveListingPrice(ev, item);

  const mediaUrl =
    item?.mediaType === "video" && item?.video
      ? `${ApiMediaURL}/${item.video}`
      : item?.image
        ? `${ApiMediaURL}/${item.image}`
        : imageUrl;

  const headline =
    name.length > 36 ? `${name.slice(0, 33)}…`.toUpperCase() : name.toUpperCase();
  const accent = (venueName || locationStr || "GET TICKETS").slice(0, 44).toUpperCase();

  return {
    id: String(item?.id ?? `slide-${index}`),
    tag: "FEATURED",
    tagColor: "bg-[#E52324]",
    headline,
    headlineAccent: accent,
    date: dateLine,
    venue: venueName || "Venue",
    city: locationStr || "",
    availability,
    startingPrice,
    image: imageUrl,
    video: videoUrl,
    eventId: ev?.id != null ? String(ev.id) : "",
    linkState: { data: item?.event, image: mediaUrl },
  };
}

function HeroSlider() {
  const dispatch = useDispatch();
  const carousalData = useSelector((state) => state.Admin.carousalData);
  const isLoding = useSelector((state) => state.Admin.adminloading);
  const [isFilterLoading, setisFilterLoading] = useState(true);

  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState(1);
  const [muted, setMuted] = useState(true);
  const [videoReady, setVideoReady] = useState(false);

  const slidesBase = useMemo(
    () => (Array.isArray(carousalData) ? carousalData.map(mapCarouselItemToSlide) : []),
    [carousalData]
  );

  const listingPrices = useBatchListingPrices(slidesBase);

  const slides = useMemo(
    () => applyStartingPrices(slidesBase, listingPrices),
    [slidesBase, listingPrices]
  );

  useEffect(() => {
    dispatch(GetAllCraousalEvent());
  }, [dispatch]);

  // Show the hero the moment its slides arrive (no artificial wait).
  useEffect(() => {
    if (!isLoding || (Array.isArray(carousalData) && carousalData.length)) setisFilterLoading(false);
  }, [carousalData, isLoding]);

  useEffect(() => {
    if (!slides.length) {
      setCurrent(0);
      return;
    }
    setCurrent((c) => Math.min(c, slides.length - 1));
  }, [slides.length]);

  const goTo = useCallback(
    (index) => {
      if (!slides.length) return;
      setDirection(index > current ? 1 : -1);
      setCurrent(index);
      setVideoReady(false);
    },
    [current, slides.length]
  );

  const next = useCallback(() => {
    if (!slides.length) return;
    setDirection(1);
    setCurrent((prev) => (prev + 1) % slides.length);
    setVideoReady(false);
  }, [slides.length]);

  const prev = useCallback(() => {
    if (!slides.length) return;
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
    setVideoReady(false);
  }, [slides.length]);

  useEffect(() => {
    if (isPaused || !slides.length) return undefined;
    const timer = setInterval(next, AUTOPLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [isPaused, next, slides.length]);

  // Other admin requests share the loading flag; once slides exist, don't hide them.
  const loading = !slidesBase.length && (isLoding || isFilterLoading);

  if (loading) {
    return (
      <div className={HERO_SECTION_CLASS}>
        <SkeletonLoading />
      </div>
    );
  }

  if (!slides.length) {
    return (
      <div className={HERO_SECTION_CLASS} aria-hidden />
    );
  }

  const slide = slides[current];
  const hasVideo = !!slide?.video;
  const ticketPath =
    slide?.eventId && slide.linkState
      ? { to: `/tickets/group.details/${slide.eventId}`, state: slide.linkState }
      : null;

  const carouselControls = (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1 sm:gap-2 min-w-0">
        {slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => goTo(i)}
            className={`transition-all duration-300 rounded-full ${
              i === current
                ? "w-6 h-1.5 sm:w-8 sm:h-2 bg-[#E52324]"
                : "w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white/30 hover:bg-white/50"
            }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
        {hasVideo && (
          <button
            type="button"
            onClick={() => setMuted(!muted)}
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-md sm:rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? (
              <MdVolumeOff className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            ) : (
              <MdVolumeUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            )}
          </button>
        )}
        <button
          type="button"
          onClick={prev}
          className="w-8 h-8 sm:w-10 sm:h-10 rounded-md sm:rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
          aria-label="Previous slide"
        >
          <HiChevronLeft className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
        </button>
        <button
          type="button"
          onClick={next}
          className="w-8 h-8 sm:w-10 sm:h-10 rounded-md sm:rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
          aria-label="Next slide"
        >
          <HiChevronRight className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
        </button>
      </div>
    </div>
  );

  const slideContent = (
    <AnimatePresence mode="wait">
      <motion.div
        key={slide.id + "-content"}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
        className="w-full max-w-[92%] sm:max-w-xl lg:max-w-2xl"
      >
        <div className="flex flex-wrap items-center gap-1.5 mb-1.5 sm:gap-2 sm:mb-5">
          <span
            className={`inline-block px-2 py-0.5 rounded-sm sm:rounded ${slide.tagColor} text-white text-[8px] sm:text-[10px] font-black uppercase tracking-[0.1em] sm:tracking-[0.15em]`}
          >
            {slide.tag}
          </span>
          {hasVideo && videoReady && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/10 backdrop-blur-sm text-white/60 text-[7px] sm:text-[9px] uppercase tracking-wide sm:tracking-wider">
              <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-[#E52324] animate-pulse" />
              Live Preview
            </span>
          )}
        </div>

        <h2 className="text-[1.05rem] leading-[1.05] sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black uppercase tracking-tight text-white mb-0.5 sm:mb-2 line-clamp-2 sm:line-clamp-3 md:line-clamp-none">
          {slide.headline}
        </h2>
        <h3 className="text-[0.95rem] leading-[1.05] sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-black uppercase tracking-tight text-[#E52324] mb-1 sm:mb-5 line-clamp-1 sm:line-clamp-2 md:line-clamp-none">
          {slide.headlineAccent}
        </h3>

        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-4 gap-y-0.5 text-[10px] sm:text-xs md:text-sm text-white/60 mb-1 sm:mb-4">
          <span className="flex items-center gap-1 sm:gap-1.5 line-clamp-1">
            <MdOutlineCalendarToday className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white/40 shrink-0" />
            {slide.date}
          </span>
          <span className="hidden sm:inline text-white/20">•</span>
          <span className="flex items-center gap-1 sm:gap-1.5 line-clamp-1">
            <MdOutlineLocationOn className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white/40 shrink-0" />
            {slide.venue}
            {slide.city ? `, ${slide.city}` : ""}
          </span>
        </div>

        <div className="flex items-center gap-1.5 text-[9px] sm:text-xs text-[#00C853] mb-1 sm:mb-6">
          <IoTicket className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
          <span className="line-clamp-1">{slide.availability}</span>
        </div>

        <div className="flex flex-wrap items-center gap-1 sm:gap-3">
          <div className="px-2.5 py-1 sm:px-4 sm:py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10">
            <span className="text-white/50 text-[9px] sm:text-xs mr-0.5 sm:mr-1">From</span>
            <span className="text-white text-sm sm:text-lg font-black">
              {slide.startingPrice > 0 ? `$${slide.startingPrice}` : "—"}
            </span>
          </div>

          {ticketPath ? (
            <>
              <Link
                to={ticketPath.to}
                state={ticketPath.state}
                className="px-3 py-1.5 sm:px-7 sm:py-3 rounded-md sm:rounded-lg bg-[#E52324] text-white text-[10px] sm:text-sm font-black uppercase tracking-wide sm:tracking-wider hover:bg-[#c91f20] transition-colors"
              >
                Get Tickets
              </Link>
              <Link
                to={ticketPath.to}
                state={ticketPath.state}
                className="hidden sm:inline-flex px-4 sm:px-7 py-2.5 sm:py-3 rounded-lg border border-white/20 text-white text-xs sm:text-sm font-black uppercase tracking-wider hover:bg-white/5 transition-colors"
              >
                View All Dates
              </Link>
            </>
          ) : (
            <>
              <span className="px-3 py-1.5 sm:px-7 sm:py-3 rounded-md sm:rounded-lg bg-white/10 text-white/40 text-[10px] sm:text-sm font-black uppercase tracking-wide sm:tracking-wider cursor-not-allowed">
                Get Tickets
              </span>
              <span className="hidden sm:inline-flex px-4 sm:px-7 py-2.5 sm:py-3 rounded-lg border border-white/10 text-white/40 text-xs sm:text-sm font-black uppercase tracking-wider cursor-not-allowed">
                View All Dates
              </span>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return (
    <section
      className={HERO_SECTION_CLASS}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background Media */}
      <AnimatePresence mode="popLayout" custom={direction}>
        <motion.div
          key={slide.id}
          custom={direction}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          className="absolute inset-0 flex items-center justify-center bg-black"
        >
          {hasVideo ? (
            <video
              key={slide.video}
              src={slide.video}
              poster={slide.image || undefined}
              autoPlay
              loop
              muted={muted}
              playsInline
              preload="auto"
              onCanPlayThrough={() => setVideoReady(true)}
              className={`absolute inset-0 ${HERO_MEDIA_CLASS}`}
            />
          ) : (
            <img
              src={slide.image}
              alt={slide.headline}
              fetchpriority={current === 0 ? "high" : undefined}
              decoding="async"
              className={HERO_MEDIA_CLASS}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />

      {/* Subtle grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Mobile: text + controls stacked at bottom (no dead gap) */}
      <div className="absolute inset-x-0 bottom-0 z-10 mx-auto w-full max-w-[1400px] px-3 pb-2 sm:hidden">
        {slideContent}
        <div className="mt-1 max-w-[92%]">{carouselControls}</div>
      </div>

      {/* Tablet/desktop: centered copy */}
      <div className="relative z-10 hidden sm:flex h-full max-w-[1400px] mx-auto px-6 lg:px-8 items-center">
        {slideContent}
      </div>

      <div className="absolute bottom-6 left-0 right-0 z-20 hidden sm:block max-w-[1400px] mx-auto px-6 lg:px-8">
        {carouselControls}
      </div>
    </section>
  );
}

export default React.memo(HeroSlider);
